import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { getTextDirection } from "@/utils/translationHelper";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const GoogleIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="16" height="16">
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
  </svg>
);

const AppleIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M16.365 1.43c0 1.14-.42 2.23-1.16 3.04-.79.87-2.08 1.55-3.13 1.47-.14-1.1.42-2.24 1.13-2.99.79-.84 2.16-1.46 3.16-1.52zM20.5 17.16c-.55 1.27-.81 1.83-1.52 2.96-.99 1.57-2.38 3.54-4.11 3.55-1.54.01-1.93-1-4.02-.99-2.09.01-2.52 1.01-4.06.99-1.73-.02-3.05-1.78-4.04-3.36C.16 16.36-.16 11.62 1.5 9.09c1.18-1.8 3.05-2.86 4.81-2.86 1.79 0 2.92 1 4.4 1 1.43 0 2.31-1.01 4.39-1.01 1.57 0 3.23.86 4.42 2.34-3.88 2.12-3.24 7.67.99 8.6z" />
  </svg>
);

export const AuthModal = ({ isOpen, onClose, onSuccess }: AuthModalProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();
  const { user, signInWithGoogle, signInWithApple, signInWithEmail, signUpWithEmail } = useAuth();
  const { t, i18n } = useTranslation();
  const direction = getTextDirection(i18n.language);

  // Auto-close once the user is authenticated (handles OAuth + email paths)
  useEffect(() => {
    if (isOpen && user) {
      onSuccess();
    }
  }, [isOpen, user, onSuccess]);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError("");
    try {
      await signInWithGoogle();
    } catch (e: any) {
      setError(e.message || t("auth.errorMessages.googleAuthFailed"));
      setIsLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setIsLoading(true);
    setError("");
    try {
      await signInWithApple();
    } catch (e: any) {
      setError(e.message || "Apple sign-in failed");
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError(t("auth.errorMessages.fillAllFields"));
      return;
    }
    if (password.length < 6) {
      setError(t("auth.errorMessages.passwordLength"));
      return;
    }

    setIsLoading(true);
    const { error: signInError } = await signInWithEmail(email, password);

    if (!signInError) {
      onSuccess();
      return;
    }

    // If the credentials are invalid, try to create a new account.
    const message = (signInError.message || "").toLowerCase();
    const isInvalidLogin =
      message.includes("invalid login") ||
      message.includes("invalid credentials") ||
      message.includes("user not found") ||
      message.includes("email not confirmed") === false && message.includes("invalid");

    if (!isInvalidLogin) {
      setError(signInError.message || t("auth.errorMessages.authenticationFailed"));
      setIsLoading(false);
      return;
    }

    const { error: signUpError } = await signUpWithEmail(email, password);
    setIsLoading(false);

    if (signUpError) {
      setError(signUpError.message || t("auth.errorMessages.authenticationFailed"));
      return;
    }

    toast({
      title: t("auth.verificationEmailSent"),
      description: t("auth.checkEmail"),
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" dir={direction}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {t("auth.welcome")}
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            {t("auth.signInDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" onClick={handleGoogleSignIn} disabled={isLoading}>
              <GoogleIcon className="h-4 w-4" /> Google
            </Button>
            <Button type="button" variant="outline" onClick={handleAppleSignIn} disabled={isLoading}>
              <AppleIcon className="h-4 w-4" /> Apple
            </Button>
          </div>

          <div className="relative flex items-center">
            <div className="flex-grow border-t border-border" />
            <span className="mx-3 text-xs text-muted-foreground">{t("auth.orContinueWith")}</span>
            <div className="flex-grow border-t border-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <div className="relative">
                <Mail className={`absolute ${direction === "rtl" ? "right-3" : "left-3"} top-3 h-4 w-4 text-muted-foreground`} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  className={direction === "rtl" ? "pr-10" : "pl-10"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <div className="relative">
                <Lock className={`absolute ${direction === "rtl" ? "right-3" : "left-3"} top-3 h-4 w-4 text-muted-foreground`} />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className={direction === "rtl" ? "pr-10" : "pl-10"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 text-destructive text-sm p-2 bg-destructive/10 rounded">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-brand-yellow hover:bg-brand-yellow/90"
              disabled={isLoading}
            >
              {isLoading ? t("auth.signingIn") : t("auth.signIn")}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              {t("auth.newHere")}
            </p>
          </form>
        </div>

        <div className="mt-4 text-center text-sm text-muted-foreground">
          {t("auth.terms.byProceedingYouAgree")}{" "}
          <Button variant="link" className="h-auto p-0" asChild>
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              {t("auth.terms.termsOfService")}
            </a>
          </Button>{" "}
          {t("auth.terms.and")}{" "}
          <Button variant="link" className="h-auto p-0" asChild>
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              {t("auth.terms.privacyPolicy")}
            </a>
          </Button>
          .
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
